import { Link, useLocation } from 'react-router-dom';
import { ExportButton } from './ExportButton';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import { isNavActive, PRIMARY_NAV, SECONDARY_NAV, type NavItem } from '../lib/nav';

interface SidebarUser {
  email: string;
  displayName?: string;
}

interface DesktopSidebarProps {
  user?: SidebarUser | null;
}

function SidebarLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const Icon = item.icon;
  const active = isNavActive(item, pathname);
  return (
    <Link
      to={item.path}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${FOCUS_RING_CLASSES} ${
        active
          ? 'bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] text-white shadow-lg'
          : 'text-[var(--text-secondary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)]'
      }`}
    >
      <Icon className="w-5 h-5 shrink-0" aria-hidden />
      <span className="font-medium">{item.label}</span>
    </Link>
  );
}

/**
 * Persistent desktop navigation (design review P2.1): at `lg:` and up the
 * hamburger/drawer pattern gives way to an always-visible left sidebar —
 * primary surfaces on top, the library section beneath, profile + export in
 * the footer. Mobile keeps the drawer.
 */
export function DesktopSidebar({ user }: DesktopSidebarProps) {
  const { pathname } = useLocation();

  const displayName = user?.displayName || user?.email?.split('@')[0] || '?';
  const initial = (user?.displayName || user?.email || '?').charAt(0).toUpperCase();

  return (
    <aside
      data-testid="desktop-sidebar"
      className="sidebar-panel hidden lg:flex fixed left-0 top-16 bottom-0 z-30 w-64 flex-col border-r border-[var(--card-border)]"
    >
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {PRIMARY_NAV.map((item) => (
          <SidebarLink key={item.id} item={item} pathname={pathname} />
        ))}
        <p className="pt-5 pb-1 px-4 text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">
          Library
        </p>
        {SECONDARY_NAV.map((item) => (
          <SidebarLink key={item.id} item={item} pathname={pathname} />
        ))}
      </nav>

      <div className="p-4 border-t border-[var(--card-border)] space-y-1">
        <Link
          to="/profile"
          className={`flex items-center gap-3 p-3 rounded-2xl hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-all ${FOCUS_RING_CLASSES}`}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--marine-400)] to-[var(--marine-600)] flex items-center justify-center text-white font-bold shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[var(--text-primary)] truncate">{displayName}</p>
            <p className="text-xs text-[var(--text-tertiary)]">View profile</p>
          </div>
        </Link>
        <ExportButton />
      </div>
    </aside>
  );
}
